import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export const PASSPORT_MIN_LENGTH = 5;
export const PASSPORT_MAX_LENGTH = 12;
/** ngx-mask for passports: up to 12 letters or digits. */
export const PASSPORT_MASK = 'A'.repeat(PASSPORT_MAX_LENGTH);
export const PASSPORT_LENGTH_MESSAGE = `El pasaporte debe tener entre ${PASSPORT_MIN_LENGTH} y ${PASSPORT_MAX_LENGTH} caracteres.`;
/** Initial value of the backoffice parameter PassportExpirationYears (used if it can't be loaded). */
export const DEFAULT_PASSPORT_EXPIRATION_YEARS = 25;

/**
 * Group validator: when the sibling document type is 'Passport', the document number must be
 * 5-12 letters/digits. Sets `passportLength` on the number control so the input shows it.
 */
export function passportNumberValidator(
  typeControlName = 'documentType',
  numberControlName = 'documentNumber',
): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const typeCtrl = group.get(typeControlName);
    const numberCtrl = group.get(numberControlName);
    if (!typeCtrl || !numberCtrl) return null;

    const value = String(numberCtrl.value ?? '').trim();
    const invalid =
      typeCtrl.value === 'Passport' &&
      value.length > 0 &&
      (value.length < PASSPORT_MIN_LENGTH || value.length > PASSPORT_MAX_LENGTH || !/^[a-zA-Z0-9]+$/.test(value));

    const current = numberCtrl.errors ?? {};
    if (invalid && !current['passportLength']) {
      numberCtrl.setErrors({ ...current, passportLength: true });
    } else if (!invalid && current['passportLength']) {
      const { passportLength: _removed, ...rest } = current;
      numberCtrl.setErrors(Object.keys(rest).length ? rest : null);
    }
    return invalid ? { passportLength: true } : null;
  };
}

/** Today + `years` as an ISO date (YYYY-MM-DD), local time. */
export function isoDatePlusYears(years: number, from: Date = new Date()): string {
  const d = new Date(from.getFullYear() + years, from.getMonth(), from.getDate());
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function parseDate(value: unknown): Date | null {
  if (typeof value !== 'string') return null;
  let m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  m = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return null;
}

/**
 * Passport expiration date: must be after today (`expired`) and no later than `maxIso` (`maxDate`).
 * Accepts ISO (YYYY-MM-DD) and display (DD/MM/YYYY) values.
 */
export function passportExpiryValidator(maxIso: () => string | null): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const date = parseDate(control.value);
    if (!date) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date <= today) return { expired: true };
    const max = parseDate(maxIso());
    if (max && date > max) return { maxDate: true };
    return null;
  };
}
